import endRecordingSound from 'assets/audios/end-recording-sound.mp3';
import startRecordingSound from 'assets/audios/start-recording-sound.mp3';
import {
  AlertTriangleIcon,
  CrossIcon,
  PauseIcon,
  PlayIcon,
  RecordIcon,
  RestartIcon,
  StopIcon,
} from 'assets/icons';
import Bowser from 'bowser';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import {
  Breakpoints,
  AlbumContentType,
  NotificationType,
  RecordingState,
  SelfRecordingStatus,
} from 'common/enums';
import { HelperText } from 'common/helper-text';
import { InfoModal } from 'common/info-modal';
import { Modal } from 'common/modal';
import { calculateRecordingModalInstructions } from 'common/sensor-permission-instructions/instructions';
import { Toggle } from 'common/toggle';
import { maxTimeClipRecordedInSeconds } from 'config/constants';
import { AppContext, SensorSelectionContext, appActions } from 'context';
import { SelfRecordingContext } from 'context/self-recording-context';
import { selfRecordingActions } from 'context/self-recording-context/action-types';
import { VideosRecordedType } from 'context/self-recording-context/context-reducer';
import { LocalStorageApi } from 'helpers/local-storage';
import {
  canUpgradeVault,
  formatRecordingTime,
  isContributor,
  isFreeTrial,
  isTemporaryUser,
  userPermissionUpgrade,
} from 'helpers/utils';
import { useLoadSensorSelection } from 'hooks/use-load-sensor-selection';
import { useMediaQuery } from 'hooks/use-media-query';
import { useSelfRecording } from 'hooks/use-self-recording';
import { NotificationObject } from 'models/notificationObject';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { WaitingRoomSensorSelection } from 'pages/vault-detail/components/video-call-waiting-room/waiting-room-sensor-selection';
import { WebcamPreview } from 'pages/vault-detail/components/video-call-waiting-room/webcam-preview';
import { useContext, useEffect, useRef, useState } from 'react';
import { isOutOfStorageError } from 'helpers/storage';
import styles from './camera-record.module.scss';

type CameraRecordProps = {
  upgradeAction?: () => void;
  startRecordingButtonRef?: React.RefObject<HTMLDivElement | null>;
  onStopRecording?: (clipResponse?: ClipSerialized) => void;
  showToggle?: boolean;
  updateUploads?: boolean;
  title?: string;
  // The save was refused because the vault is out of storage.
  onOutOfStorage?: () => void;
};

const CameraRecord: React.FC<CameraRecordProps> = ({
  upgradeAction,
  startRecordingButtonRef,
  onStopRecording,
  showToggle = true,
  updateUploads = true,
  title,
  onOutOfStorage,
}) => {
  const { state: selfRecordingState, dispatch } =
    useContext(SelfRecordingContext);
  const { state, dispatch: generalDispatch } = useContext(AppContext);
  const { subscriptionsScheme } = state;
  const isMobile = useMediaQuery(`(max-width: ${Breakpoints.sm}px)`);
  const [timer, setTimer] = useState(0);
  const progressBarStyle = {
    width: `${(timer / maxTimeClipRecordedInSeconds) * 100}%`,
  };
  const {
    mediaRecorderState,
    countingTime,
    retryFailedRequests,
    closeAllTracks,
    createMediaRecorder,
    handlePauseRecording,
    handleResumeRecording,
    handleRestartRecording,
  } = useSelfRecording();
  const [continueInNewClip, setContinueInNewClip] = useState(showToggle);
  const endRecordingSoundRef = useRef<HTMLAudioElement>(null);
  const startRecordingSoundRef = useRef<HTMLAudioElement>(null);

  const [showErrorModal, setShowErrorModal] = useState(false);
  const {
    sensorSelection: {
      microphone: { error: microphoneError, enabled: micEnabled },
      camera: { error: cameraError, enabled: cameraEnabled },
    },
  } = useContext(SensorSelectionContext);
  useLoadSensorSelection();

  const { browser } = Bowser.parse(window.navigator.userAgent);
  const continueInNewClipPatchForSafari = browser.name === 'Safari';

  const [showToggleMessage, setShowToggleMessage] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showMediaErrorModal, setShowMediaErrorModal] = useState(false);

  const [errorModalHeader, errorModalContent] =
    calculateRecordingModalInstructions(microphoneError, cameraError);

  const missingFunctionality = !!microphoneError || !!cameraError;
  const positionInBatchRef = useRef<number | undefined>(undefined);
  const isUploading = useRef(false);

  let myInterval: NodeJS.Timeout;

  const canStartRecording = () =>
    state.currentVault && cameraEnabled && micEnabled;

  const showMediaErrorModalComponent = () => (
    <InfoModal
      className={styles.modalMediaError}
      title="Camera & Mic"
      content={
        <div className={styles.modalMediaErrorContent}>
          <HelperText
            icon={<AlertTriangleIcon className={styles.modalMediaErrorIcon} />}
            content={
              <span className="text__body__regular__large__textNeutral50">
                {errorModalHeader}
              </span>
            }
          />
          <p>{errorModalContent}</p>
        </div>
      }
      onClose={() => setShowMediaErrorModal(false)}
    />
  );
  const getInformationById = (requestId: string) => {
    const videoInformation = selfRecordingState.videosRecorded.find(
      (video) => video.requestId === requestId,
    );
    return videoInformation;
  };

  const createClip = async (requestId: string) => {
    const clipToSave = getInformationById(requestId);
    if (!clipToSave) {
      generalDispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Something went wrong',
          message: 'Please try again',
          type: NotificationType.Error,
        }),
      });
      return undefined;
    }
    const clipTitle = title?.trim();
    const createClipRequest: CreateClipRequestType = {
      clip: {
        videoSignedId: clipToSave.videoSignedId,
        name: clipTitle || clipToSave.name || 'Self Recording',
        recordedAt: new Date(Date.now() + parseInt(requestId, 10) * 1000),
        batchId: selfRecordingState.batchId,
        positionInBatch: clipToSave.positionInBatchToClip
          ? clipToSave.positionInBatchToClip
          : positionInBatchRef.current,
        albumId: selfRecordingState.albumId,
      },
    };

    try {
      const guestToken = LocalStorageApi.get('guestToken');
      const clipResponse = guestToken
        ? await VaultsController.createClipByGuest(
            selfRecordingState.vaultId,
            createClipRequest,
          )
        : await VaultsController.createClip(
            selfRecordingState.vaultId,
            createClipRequest,
          );
      if (clipResponse && updateUploads) {
        dispatch({
          type: selfRecordingActions.setUploadResponse,
          uploadResponse: {
            uploadId: requestId,
            response: {
              ...clipResponse,
              user: state.data.user,
              contentType: AlbumContentType.video,
              fileUrl: clipResponse.videoUrl,
            },
          },
        });
      }
      return clipResponse;
    } catch (error) {
      // Over the vault's storage: the host shows its out-of-storage dialog.
      if (onOutOfStorage && isOutOfStorageError(error)) {
        onOutOfStorage();
        return undefined;
      }
      generalDispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Something went wrong',
          message: 'Please try again',
          type: NotificationType.Error,
        }),
      });
      return undefined;
    }
  };

  const showNotificationButtons = (buttonStyle: ButtonStyle) => (
    <div className={styles.notificationButtons}>
      <Button
        buttonSize={ButtonSize.Small}
        buttonStyle={buttonStyle}
        onClick={upgradeAction}
      >
        Upgrade
      </Button>
    </div>
  );

  const checkShowMessage = () => {
    if (!subscriptionsScheme || !Array.isArray(subscriptionsScheme)) {
      return;
    }

    const contributor =
      state.currentVault && isContributor(state.data.user, state.currentVault);
    if (!state.currentVault) {
      return;
    }
    const isTrial = isFreeTrial(state.currentVault);
    // TODO: change this to the correct definition using percentages
    const isFull = false;
    const isAlmostFull = false;
    const fullTitle = isTrial
      ? 'Your trial vault is full'
      : 'Your vault is full';
    let fullMessage = '';
    if (isTrial) {
      fullMessage = `${fullMessage}${
        contributor
          ? 'Contact the vault owner to upgrade their subscription to keep recording.'
          : 'Upgrade to a subscription to increase your limit.'
      }`;
    } else if (contributor) {
      fullMessage = `${fullMessage} Contact the vault owner to purchase extra storage or upgrade their subscription.`;
    } else {
      fullMessage =
        // TODO: change this to the correct message
        `${fullMessage}You’ve recorded ${
          selfRecordingState.videosRecorded.length
        }
      out of storage. Get extra storage by purchasing more${
        state.currentVault &&
        canUpgradeVault(state.currentVault, subscriptionsScheme) &&
        'or upgrading your subscription'
      }.`;
    }

    const reachingLimitTitle = isTrial
      ? 'Your trial vault is almost full'
      : 'Your vault is almost full';
    let reachingLimitMessage =
      // TODO: change this to the correct message
      `You've recorded ${selfRecordingState.videosRecorded.length} `;
    if (isTrial) {
      reachingLimitMessage = `${reachingLimitMessage} Upgrade to a subscription to increase your limit.`;
    } else if (contributor) {
      reachingLimitMessage = `${reachingLimitMessage} Contact the vault owner to purchase extra storage or upgrade their subscription.`;
    } else {
      reachingLimitMessage = `${reachingLimitMessage} Get extra storage by purchasing more${
        state.currentVault &&
        canUpgradeVault(state.currentVault, subscriptionsScheme)
          ? ' or upgrading your subscription'
          : ''
      }.`;
    }

    if (!showWarning && userPermissionUpgrade(state.data.user)) {
      if (isAlmostFull) {
        generalDispatch({
          type: appActions.NOTIFICATION,
          notification: new NotificationObject({
            show: true,
            title: reachingLimitTitle,
            message: reachingLimitMessage,
            type: NotificationType.Info,
            footer:
              !contributor && showNotificationButtons(ButtonStyle.PrimaryGhost),
            limitTimeMs: 1000 * 15,
          }),
        });
      }
      if (isFull) {
        setShowWarning(true);
        generalDispatch({
          type: appActions.NOTIFICATION,
          notification: new NotificationObject({
            show: true,
            title: fullTitle,
            message: fullMessage,
            type: NotificationType.Error,
            footer:
              !contributor &&
              showNotificationButtons(ButtonStyle.SecondaryGhost),
            limitTimeMs: 1000 * 15,
          }),
        });
      }
    }
  };

  const startRecord = async () => {
    startRecordingSoundRef.current?.play();
    setTimer(0);
    const mediaRecorder = await createMediaRecorder();
    if (mediaRecorder) {
      mediaRecorder.start();
      dispatch({ type: selfRecordingActions.setClipNumber, clipNumber: 1 });
    }
  };

  useEffect(() => {
    setShowMediaErrorModal(missingFunctionality);
  }, [missingFunctionality]);

  useEffect(() => {
    dispatch({ type: selfRecordingActions.setReadyForNextStep });
  }, []);

  const addPositionInBatch = (video: VideosRecordedType) => {
    if (positionInBatchRef.current) {
      positionInBatchRef.current += 1;
      dispatch({
        type: selfRecordingActions.setPositionInBatchToClip,
        positionInBatchToClip: {
          positionInBatch: positionInBatchRef.current,
          requestId: video.requestId,
        },
      });
    }
  };

  const handleSaveAllClips = async () => {
    selfRecordingState.videosRecorded.forEach(async (video) => {
      if (updateUploads)
        dispatch({
          type: selfRecordingActions.addUpload,
          addUpload: {
            type: AlbumContentType.video,
            data: 'Self Recording',
            isUploading: true,
            uploadId: video.requestId,
          },
        });
      addPositionInBatch(video);
      const savedSuccessfully = await createClip(video.requestId);
      onStopRecording?.(savedSuccessfully);
      if (savedSuccessfully) {
        dispatch({
          type: selfRecordingActions.deleteVideo,
          videoToDelete: video.requestId,
        });
        if (updateUploads)
          dispatch({
            type: selfRecordingActions.updateUpload,
            updateUpload: {
              isUploading: false,
              error: false,
              uploadId: video.requestId,
              type: AlbumContentType.video,
              data: 'Self Recording',
            },
          });
      } else if (updateUploads)
        dispatch({
          type: selfRecordingActions.updateUpload,
          updateUpload: {
            isUploading: false,
            error: true,
            uploadId: video.requestId,
            type: AlbumContentType.video,
            data: 'Self Recording',
          },
        });
    });
  };

  const handleStop = () => {
    endRecordingSoundRef.current?.play();
    mediaRecorderState?.stop();
    dispatch({ type: selfRecordingActions.setClipNumber, clipNumber: 0 });
    checkShowMessage();
    isUploading.current = true;
    dispatch({
      type: selfRecordingActions.setIsUploading,
      isUploading: true,
    });
    setTimer(0);
  };

  const handlePause = () => {
    handlePauseRecording();
  };

  const handleResume = () => {
    handleResumeRecording();
  };

  const handleRestart = () => {
    handleRestartRecording();
    setTimer(0);
  };

  const isPaused = selfRecordingState.recordingState === RecordingState.PAUSED;
  const isRecordingActive =
    selfRecordingState.recordingState === RecordingState.RECORDING;
  const isRecordingOrPaused = isRecordingActive || isPaused;

  useEffect(() => {
    if (isUploading.current && selfRecordingState.readyForNextStep) {
      isUploading.current = false;
      closeAllTracks();
      handleSaveAllClips();

      dispatch({
        type: selfRecordingActions.setIsUploading,
        isUploading: false,
      });
    }
  }, [selfRecordingState.readyForNextStep]);

  useEffect(() => {
    checkShowMessage();
    return closeAllTracks;
  }, []);

  useEffect(() => {
    if (subscriptionsScheme && Array.isArray(subscriptionsScheme)) {
      checkShowMessage();
    }
  }, [subscriptionsScheme]);

  useEffect(() => {
    const processing = selfRecordingState.videosRecorded.some(
      (video) => video.status === SelfRecordingStatus.processing,
    );
    const haveErrors = selfRecordingState.videosRecorded.some(
      (video) => video.status === SelfRecordingStatus.error,
    );
    if (!processing && haveErrors) {
      isUploading.current = false;

      dispatch({
        type: selfRecordingActions.setIsUploading,
        isUploading: false,
      });
      setShowErrorModal(true);
    }
  }, [selfRecordingState.videosRecorded]);

  useEffect(() => {
    const handleTimer = () => {
      if (timer === maxTimeClipRecordedInSeconds - 30 && !continueInNewClip) {
        setShowToggleMessage(true);
      }
      if (timer === maxTimeClipRecordedInSeconds) {
        if (mediaRecorderState && continueInNewClip && canStartRecording()) {
          const stopPromise = new Promise<void>((resolve) => {
            const originalOnStop = mediaRecorderState.onstop;
            mediaRecorderState!.onstop = (ev) => {
              if (originalOnStop) {
                originalOnStop.call(mediaRecorderState, ev);
              }
              resolve();
            };
          });
          mediaRecorderState?.stop();

          stopPromise.then(() => {
            checkShowMessage();
            setTimeout(() => {
              mediaRecorderState?.start();
              dispatch({
                type: selfRecordingActions.setClipNumber,
                clipNumber: selfRecordingState.clipNumber + 1,
              });
            }, 100);
          });
        } else {
          handleStop();
          checkShowMessage();
        }
      }
    };
    handleTimer();
  }, [timer, continueInNewClip]);

  useEffect(() => {
    if (countingTime) {
      myInterval = setInterval(() => {
        setTimer((t) => t + 1);
      }, 1000);
    }
    return () => {
      clearInterval(myInterval);
    };
  }, [countingTime]);

  const errorModal = () => (
    <Modal
      disableManualClosing
      contentClassName={styles.errorContent}
      className={styles.waitingModal}
    >
      <div className={styles.errorModal}>
        <CrossIcon className={styles.crossIcon} />
        <div className={styles.messageAndButton}>
          <p className="text__body__regular__medium__gray800">
            Oh no. Something went wrong. Please try again.
          </p>
          <div className={styles.button}>
            <Button
              buttonSize={ButtonSize.Small}
              buttonStyle={ButtonStyle.RedGhost}
              onClick={() => {
                setShowErrorModal(false);
                dispatch({
                  type: selfRecordingActions.deleteAllVideos,
                });
              }}
            >
              Cancel recording
            </Button>
            <Button
              buttonSize={ButtonSize.Small}
              buttonStyle={ButtonStyle.PrimaryFilled}
              onClick={() => {
                setShowErrorModal(false);
                retryFailedRequests();
                isUploading.current = true;
              }}
            >
              Retry
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );

  const renderRecordingControls = () => (
    <div className={styles.recordingControlsBar}>
      <button
        type="button"
        className={styles.stopButton}
        onClick={handleStop}
        aria-label="Stop recording"
      >
        <StopIcon className={styles.stopButtonIcon} />
        <span className="text__body__semi__bold__small__error600">Stop</span>
      </button>
      <div className={styles.controlsDivider} />
      <button
        type="button"
        className={styles.controlButton}
        onClick={isPaused ? handleResume : handlePause}
        aria-label={isPaused ? 'Resume recording' : 'Pause recording'}
      >
        {isPaused ? (
          <PlayIcon className={styles.controlButtonIcon} />
        ) : (
          <PauseIcon className={styles.controlButtonIcon} />
        )}
      </button>
      <button
        type="button"
        className={styles.controlButton}
        onClick={handleRestart}
        aria-label="Restart recording"
      >
        <RestartIcon className={styles.controlButtonIcon} />
      </button>
    </div>
  );

  const showMobileLayout = () => (
    <>
      <div className={styles.timeAndLine}>
        <span className="text__body__regular__medium__gray800">
          {formatRecordingTime(timer)}
        </span>
        <div className={styles.progressBar}>
          <div className={styles.progress} style={progressBarStyle} />
        </div>
        <span className="text__body__regular__medium__gray800">
          {formatRecordingTime(maxTimeClipRecordedInSeconds)}
        </span>
      </div>
      {isRecordingOrPaused ? (
        renderRecordingControls()
      ) : (
        <div ref={startRecordingButtonRef} id="startRecordingButton">
          <Button
            buttonSize={ButtonSize.Medium}
            buttonStyle={ButtonStyle.PrimaryFilled}
            onClick={startRecord}
            disabled={!canStartRecording()}
            className={styles.recordButton}
            tooltipContent="Limit reached, available after sign up"
            tooltipPosition="top"
            tooltip={!canStartRecording() && isTemporaryUser(state.data.user)}
            tooltipId="camera-microphone-tooltip"
          >
            <PlayIcon />
            Start Recording
          </Button>
        </div>
      )}
      {showToggle && (
        <div className={styles.toggleAndText}>
          <Toggle
            id="switch"
            tooltipText={
              continueInNewClipPatchForSafari
                ? 'Continuing a recording is not currently available for Safari browsers due to performance issues. We will be releasing an update in the near future.'
                : 'Your time is running out. Click on this toggle to continue recording in a new clip.'
            }
            checked={!continueInNewClipPatchForSafari && continueInNewClip}
            disabled={continueInNewClipPatchForSafari}
            onChangeFn={() => {
              setContinueInNewClip(
                !continueInNewClipPatchForSafari && !continueInNewClip,
              );
            }}
            showTooltip={showToggleMessage}
          />
          <span className="text__body__regular__medium__gray700">
            Continue in new clip if time runs out
          </span>
        </div>
      )}
      <WaitingRoomSensorSelection />
    </>
  );

  return (
    <div className={styles.allPage}>
      {showErrorModal && errorModal()}
      {showMediaErrorModal && showMediaErrorModalComponent()}
      <WebcamPreview
        className={styles.camera}
        isRecording={isRecordingActive}
        showButtons={false}
        clipNumber={selfRecordingState.clipNumber}
      />
      {isMobile ? (
        showMobileLayout()
      ) : (
        <div className={styles.recordSection}>
          <div className={styles.timeLineAndRecordButton}>
            <div className={styles.timeAndLine}>
              <span className="text__body__regular__medium__gray800">
                {formatRecordingTime(timer)}
              </span>
              <div className={styles.progressBar}>
                <div className={styles.progress} style={progressBarStyle} />
              </div>
              <span className="text__body__regular__medium__gray800">
                {formatRecordingTime(maxTimeClipRecordedInSeconds)}
              </span>
            </div>
            {isRecordingOrPaused ? (
              renderRecordingControls()
            ) : (
              <div ref={startRecordingButtonRef} id="startRecordingButton">
                <Button
                  buttonSize={ButtonSize.Medium}
                  buttonStyle={ButtonStyle.Primary}
                  onClick={startRecord}
                  disabled={!canStartRecording()}
                  tooltipContent="Limit reached, available after sign up"
                  tooltipPosition="top"
                  tooltip={
                    !canStartRecording() && isTemporaryUser(state.data.user)
                  }
                  tooltipId="camera-microphone-tooltip"
                >
                  <div className={styles.recordButton}>
                    <RecordIcon />
                    Start Recording
                  </div>
                </Button>
              </div>
            )}
          </div>
          <div className={styles.SensorsAndToggle}>
            <WaitingRoomSensorSelection />
            {showToggle && (
              <div className={styles.toggleAndText}>
                <Toggle
                  id="switch"
                  tooltipText={
                    continueInNewClipPatchForSafari
                      ? 'Continuing a recording is not currently available for Safari browsers due to performance issues. We will be releasing an update in the near future.'
                      : 'Your time is running out. Click on this toggle to continue recording in a new clip.'
                  }
                  checked={continueInNewClip}
                  disabled={continueInNewClipPatchForSafari}
                  onChangeFn={() => {
                    setContinueInNewClip(
                      !continueInNewClipPatchForSafari && !continueInNewClip,
                    );
                  }}
                  showTooltip={showToggleMessage}
                />
                <span className="text__body__regular__medium__gray700">
                  Continue in new clip if time runs out
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      <audio ref={startRecordingSoundRef} hidden>
        <source src={startRecordingSound} type="audio/mp3" />
        <track kind="captions" />
      </audio>
      <audio ref={endRecordingSoundRef} hidden>
        <source src={endRecordingSound} type="audio/mp3" />
        <track kind="captions" />
      </audio>
    </div>
  );
};
export { CameraRecord };
